import type { Event } from '@/lib/api/types'

/**
 * The one place promotions print money and menu-unit amounts.
 *
 * Menu units are the units of an item's own `price` on every event type;
 * currency is menu units × the event's `token_value` (vendor/pricing.py).
 * Currency prints en-CA with the narrow symbol ("$3.00" for CAD and USD).
 * Tokens print as whole tokens on a token event (rounded down, as the Menu
 * screen does) and with at most two decimals anywhere else, never as a raw
 * float.
 */

export interface MenuUnits {
  eventType: NonNullable<Event['event_type']>
  /** Whole tokens only, and no percent off. */
  tokenEvent: boolean
  /** Currency per menu unit; null when the event has none. */
  tokenValue: number | null
  currency: string
}

export function menuUnits(event: Pick<Event, 'event_type' | 'token_value' | 'currency'>): MenuUnits {
  const eventType = event.event_type ?? 'token'
  // A DecimalField: the API sends it as a string ("2.00").
  const tokenValue = Number(event.token_value)
  return {
    eventType,
    tokenEvent: eventType === 'token',
    tokenValue: Number.isFinite(tokenValue) && tokenValue > 0 ? tokenValue : null,
    currency: event.currency || 'CAD',
  }
}

// The Menu screen's token value while the event has none (`token_value || 2.0`).
const MENU_DEFAULT_TOKEN_VALUE = 2

const currencyFormats = new Map<string, Intl.NumberFormat>()

export function formatCurrency(amount: number, currency?: string | null): string {
  const code = (currency || 'CAD').toUpperCase()
  let fmt = currencyFormats.get(code)
  if (!fmt) {
    try {
      fmt = new Intl.NumberFormat('en-CA', { style: 'currency', currency: code, currencyDisplay: 'narrowSymbol' })
    } catch {
      return `$${amount.toFixed(2)}`
    }
    currencyFormats.set(code, fmt)
  }
  return fmt.format(amount)
}

/**
 * Rounds down to the cent, as `apply_rule` does to a rule's currency amount.
 * The product is first cut to six places so float noise (1.33 × 3 =
 * 3.9899999999999998) does not lose a cent.
 */
export function floorToCent(amount: number): number {
  return Math.floor(Number((amount * 100).toFixed(6))) / 100
}

const TOKEN_NUMBER = new Intl.NumberFormat('en-CA', { maximumFractionDigits: 2, useGrouping: false })

/** A token count: whole tokens (rounded down) when `wholeTokens`, else at most two decimals. */
export function formatTokenNumber(value: number, wholeTokens: boolean): string {
  return TOKEN_NUMBER.format(wholeTokens ? Math.floor(value) : value)
}

export interface MenuPriceOptions {
  /**
   * Round the currency down to the cent instead of to the nearest cent: for
   * a set price typed or stored on a rule, which the backend floors. Prices
   * the backend computed (preview, menu) are already floored and round back
   * to the same cent.
   */
  floor?: boolean
}

/** A menu-unit amount: currency on a Direct Pay event, tokens otherwise. */
export function formatMenuPrice(value: number | null | undefined, units: MenuUnits, options: MenuPriceOptions = {}): string {
  if (value == null || !Number.isFinite(value)) return '—'
  if (units.eventType === 'direct_pay') {
    const amount = value * (units.tokenValue ?? MENU_DEFAULT_TOKEN_VALUE)
    return formatCurrency(options.floor ? floorToCent(amount) : amount, units.currency)
  }
  const tokens = formatTokenNumber(value, units.tokenEvent)
  return `${tokens} ${tokens === '1' ? 'token' : 'tokens'}`
}
